import { createFileRoute, isRedirect, Outlet, redirect } from "@tanstack/react-router";
import axiosInstance from "api/axiosInstance.ts";
import queryClient from "api/queryClient.ts";
import tenantAxiosInstance from "api/tenantAxiosInstance.ts";
import { mergeAuthenticatedUser } from "api/User/mergeAuthenticatedUser.ts";
import { getMeSessionQueryOptions, getMeTenantsQueryOptions } from "api/User/queryOptions.ts";
import { clearAllTableConfigs } from "components/UI/Table/hooks/useTableLocalStorage.ts";
import { getSessionFromStorage, removeSession, setSessionInStorage } from "hooks/useSession.ts";
import i18n from "i18next";
import { toast } from "react-toastify";
import { consumeDrawerNavigationBypass } from "utils/drawerNavigationBypass";
import { tenantExists } from "utils/tenantExists.ts";

const NON_TENANT_SEGMENTS = new Set(["auth", "account"]);

async function getLoginRedirect(pathname: string) {
    const firstSegment = pathname.split("/").filter(Boolean)[0];

    if (!firstSegment || NON_TENANT_SEGMENTS.has(firstSegment)) {
        return redirect({ to: "/auth/login", replace: true });
    }

    if (!(await tenantExists(firstSegment))) {
        // beforeLoad can run more than once for the same visit (route re-matching while the
        // redirect below settles) — a fixed toastId stops react-toastify from stacking duplicates.
        toast.error(i18n.t("common.errors.tenant_not_found"), { toastId: "tenant-not-found" });
        return redirect({ to: "/auth/login", replace: true });
    }

    return redirect({
        to: "/$tenantSlug/auth/login",
        params: { tenantSlug: firstSegment },
        replace: true,
    });
}

async function clearSessionAndRedirect(pathname: string): Promise<never> {
    const redirectTo = await getLoginRedirect(pathname);
    queryClient.clear();
    clearAllTableConfigs();
    removeSession();
    throw redirectTo;
}

export const Route = createFileRoute("/_authenticated")({
    component: () => <Outlet />,
    beforeLoad: async ({ location }) => {
        const currentSession = getSessionFromStorage();
        const { token, user } = currentSession;

        if (!token) {
            await clearSessionAndRedirect(location.pathname);
        }

        axiosInstance.setAuthorization(token);
        tenantAxiosInstance.setAuthorization(token);

        if (user && consumeDrawerNavigationBypass(location.pathname)) {
            setSessionInStorage({ token, user, activeTenantSlug: currentSession.activeTenantSlug ?? null });
            return;
        }

        try {
            const meQueryOptions = getMeSessionQueryOptions();
            const tenantsQueryOptions = getMeTenantsQueryOptions();
            const [{ user: currentUser }, { user: userWithTenants }] = await Promise.all([
                queryClient.ensureQueryData({ ...meQueryOptions, revalidateIfStale: true }),
                queryClient.fetchQuery(tenantsQueryOptions),
            ]);

            if (!currentUser || !userWithTenants) {
                await clearSessionAndRedirect(location.pathname);
            }

            const activeTenantPermissions = currentSession.user?.tenants?.find(
                (tenant) => tenant.slug_name === currentSession.activeTenantSlug
            )?.userPermissions;
            const user = mergeAuthenticatedUser(
                currentUser,
                userWithTenants,
                currentSession.activeTenantSlug,
                activeTenantPermissions
            );

            setSessionInStorage({ token, user, activeTenantSlug: currentSession.activeTenantSlug ?? null });
            axiosInstance.setAuthorization(token);
            tenantAxiosInstance.setAuthorization(token);
        } catch (error) {
            if (isRedirect(error)) throw error;

            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status === 401 || status === 403) {
                await clearSessionAndRedirect(location.pathname);
            }
            throw error;
        }
    },
});
